export interface InterfaceBuilderComponentDraftSelection {
  kind: "component";
  componentId: string;
  componentKind: string;
  componentPath: string | null;
  id: string;
}

export interface InterfaceBuilderComponentPropertyDraft {
  propertyPath: readonly string[];
  selection: InterfaceBuilderComponentDraftSelection;
  selectionId: string;
  value: unknown;
}

interface ApplyComponentPropertyDraftsParams {
  componentId: string;
  drafts: readonly InterfaceBuilderComponentPropertyDraft[];
  selectionBasePath?: readonly number[];
}

export function upsertComponentPropertyPreviewDraft(
  drafts: readonly InterfaceBuilderComponentPropertyDraft[],
  draft: InterfaceBuilderComponentPropertyDraft,
  originalValue: unknown,
): readonly InterfaceBuilderComponentPropertyDraft[] {
  const nextDrafts = drafts.filter((currentDraft) => (
    currentDraft.selectionId !== draft.selectionId
    || !arePathsEqual(currentDraft.propertyPath, draft.propertyPath)
  ));

  if (areUnknownValuesEqual(originalValue, draft.value)) {
    return nextDrafts;
  }

  return [
    ...nextDrafts,
    {
      ...draft,
      propertyPath: [...draft.propertyPath],
      value: cloneUnknownValue(draft.value),
    },
  ];
}

export function removeComponentPropertyPreviewDraftsForComponent(
  drafts: readonly InterfaceBuilderComponentPropertyDraft[],
  componentId: string,
): readonly InterfaceBuilderComponentPropertyDraft[] {
  return drafts.filter((draft) => (
    draft.selection.componentId !== componentId
  ));
}

export function applyComponentPropertyDraftsToScheme<
  T extends Record<string, unknown>,
>(
  scheme: T,
  {
    componentId,
    drafts,
    selectionBasePath = [],
  }: ApplyComponentPropertyDraftsParams,
): T | Record<string, unknown> {
  let nextScheme: Record<string, unknown> = scheme;

  for (const draft of drafts) {
    if (draft.selection.componentId !== componentId) continue;

    const relativePath = readRelativeComponentPath(
      draft.selection.componentPath,
      selectionBasePath,
    );
    if (!relativePath) continue;

    nextScheme = writeComponentPropertyDraftToScheme(
      nextScheme,
      relativePath,
      draft.propertyPath,
      draft.value,
    );
  }

  return nextScheme;
}

function readRelativeComponentPath(
  componentPath: string | null,
  selectionBasePath: readonly number[],
): readonly number[] | null {
  const path = parseComponentPath(componentPath);
  if (!path) return null;
  if (path.length < selectionBasePath.length) return null;
  const hasBasePath = selectionBasePath.every((segment, index) => (
    path[index] === segment
  ));
  if (!hasBasePath) return null;

  return path.slice(selectionBasePath.length);
}

function parseComponentPath(componentPath: string | null): readonly number[] | null {
  if (componentPath === null || componentPath === "") return [];

  const path = componentPath.split(".").map((segment) => Number(segment));
  return path.every((segment) => Number.isInteger(segment) && segment >= 0)
    ? path
    : null;
}

function writeComponentPropertyDraftToScheme(
  scheme: Record<string, unknown>,
  componentPath: readonly number[],
  propertyPath: readonly string[],
  value: unknown,
): Record<string, unknown> {
  if (componentPath.length === 0) {
    return writePropertyToSchemeNode(scheme, propertyPath, value);
  }

  const [childIndex, ...tail] = componentPath;
  const children = readSchemeChildren(scheme);
  const child = children[childIndex];
  if (!isRecord(child)) return scheme;

  const nextChild = writeComponentPropertyDraftToScheme(
    child,
    tail,
    propertyPath,
    value,
  );
  if (nextChild === child) return scheme;

  const nextChildren = [...children];
  nextChildren[childIndex] = nextChild;

  return {
    ...scheme,
    children: nextChildren,
  };
}

function writePropertyToSchemeNode(
  node: Record<string, unknown>,
  propertyPath: readonly string[],
  value: unknown,
): Record<string, unknown> {
  const nextNode = writeNestedProperty(node, propertyPath, value);
  if (!isRecord(node.props)) return nextNode;

  return {
    ...nextNode,
    props: writeNestedProperty(node.props, propertyPath, value),
  };
}

function writeNestedProperty(
  source: Record<string, unknown>,
  path: readonly string[],
  value: unknown,
): Record<string, unknown> {
  if (path.length === 0) return source;

  const [head, ...tail] = path;
  if (tail.length === 0) {
    return {
      ...source,
      [head]: cloneUnknownValue(value),
    };
  }

  const currentValue = source[head];
  const currentRecord = isRecord(currentValue) ? currentValue : {};

  return {
    ...source,
    [head]: writeNestedProperty(currentRecord, tail, value),
  };
}

function readSchemeChildren(value: Record<string, unknown>): readonly unknown[] {
  if (Array.isArray(value.children)) return value.children;
  return isRecord(value.props) && Array.isArray(value.props.children)
    ? value.props.children
    : [];
}

function arePathsEqual(
  left: readonly string[],
  right: readonly string[],
): boolean {
  return left.length === right.length
    && left.every((segment, index) => segment === right[index]);
}

function areUnknownValuesEqual(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) return true;

  if (Array.isArray(left) || Array.isArray(right)) {
    if (!Array.isArray(left) || !Array.isArray(right)) return false;
    return left.length === right.length
      && left.every((item, index) => areUnknownValuesEqual(item, right[index]));
  }

  if (!isRecord(left) || !isRecord(right)) return false;

  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  if (leftKeys.length !== rightKeys.length) return false;

  return leftKeys.every((key) => (
    Object.prototype.hasOwnProperty.call(right, key)
    && areUnknownValuesEqual(left[key], right[key])
  ));
}

function cloneUnknownValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => cloneUnknownValue(item));
  }
  if (!isRecord(value)) return value;

  return Object.entries(value).reduce<Record<string, unknown>>(
    (clone, [key, fieldValue]) => {
      clone[key] = cloneUnknownValue(fieldValue);
      return clone;
    },
    {},
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
